import { client, getToken } from "./client";

const authHeaders = () => ({
  Authorization: getToken(),
});

export interface JsonlImportList {
  content: JsonlImportRecord[];
  totalElements: number;
}

export interface JsonlImportRecord {
  id: string;
  originalFilename: string;
  status: string;
  startedAt?: string;
  finishedAt?: string;
  totalLines: number;
  processedLines: number;
  correct: number;
  incorrect: number;
  warnings?: number;
  percentCompleted: number;
  resultSummary?: string;
  userEmail?: string;
}

export interface JsonlDownloadLink {
  downloadUrl: string;
  filename: string;
}

export interface JsonlImportClient {
  upload: (
    file: File,
    start: boolean,
    businessUnitId?: number
  ) => Promise<{ processId: string; originalFilename?: string }>;
  list: (page: number, size: number) => Promise<JsonlImportList>;
  progress: (processId: string) => Promise<JsonlImportRecord>;
  cancel: (processId: string) => Promise<void>;
  remove: (processId: string) => Promise<void>;
  start: (processId: string) => Promise<void>;
  errorsUrl: (processId: string) => Promise<JsonlDownloadLink>;
  originalUrl: (processId: string) => Promise<JsonlDownloadLink>;
  preview: (
    processId: string,
    page: number,
    size: number,
    search?: string
  ) => Promise<unknown>;
}

export const createJsonlImportClient = (basePath: string): JsonlImportClient => ({
  upload: async (file, start, businessUnitId) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("start", String(start));
    if (businessUnitId != null) {
      formData.append("businessUnitId", String(businessUnitId));
    }
    const response = await client.post(`${basePath}/import/upload`, formData, {
      headers: {
        ...authHeaders(),
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data.data;
  },
  list: async (page, size) => {
    const response = await client.get(
      `${basePath}/import?page=${page}&size=${size}`,
      { headers: authHeaders() }
    );
    return response.data.data;
  },
  progress: async (processId) => {
    const response = await client.get(`${basePath}/import/progress/${processId}`, {
      headers: authHeaders(),
    });
    return response.data;
  },
  cancel: async (processId) => {
    await client.post(`${basePath}/import/cancel/${processId}`, {}, {
      headers: authHeaders(),
    });
  },
  remove: async (processId) => {
    await client.delete(`${basePath}/import/${processId}`, {
      headers: authHeaders(),
    });
  },
  start: async (processId) => {
    await client.post(`${basePath}/import/start/${processId}`, {}, {
      headers: authHeaders(),
    });
  },
  errorsUrl: async (processId) => {
    const response = await client.get(
      `${basePath}/import/result/${processId}/errors.csv/url`,
      { headers: authHeaders() }
    );
    return response.data;
  },
  originalUrl: async (processId) => {
    const response = await client.get(
      `${basePath}/import/result/${processId}/original/url`,
      { headers: authHeaders() }
    );
    return response.data;
  },
  preview: async (processId, page, size, search) => {
    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
    });
    if (search) {
      params.set("search", search);
    }
    const response = await client.get(
      `${basePath}/import/result/${processId}/preview?${params.toString()}`,
      { headers: authHeaders() }
    );
    return response.data.data;
  },
});
